import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { EiqTextarea } from 'eiq-storybook';
import moment from 'moment';

import { 
  useDeleteDatasheetQuestionResultComment,
  useAddDatasheetQuestionResultComment,
  useUpdateDatasheetQuestionResultComment,
  useDatasheetQuestionResultCommentsLazy
} from 'src/hooks/jobDatasheet';
import { notification } from 'src/components/Notification';
import Loader from 'src/components/Loader';
import { resultUnique } from 'src/components/JobDatasheet/utils';
import {
  VERIFIER_REVISION_UNDERWAY,
  REVIEWER_REVIEW_UNDERWAY,
} from 'src/utils/data/job';
import s from './CommentsSection.styles';

export const CommentsSection = ({
  canAddComment = false,
  currentUserId,
  datasheetQuestionResultId,
  selectedQuestion,
  currentResult,
  updatedScroll,
  status,
  setUpdatedCommentStatus,
  storedResult,
  defaultResults,
  datasheetQuestionId,
  setResult,
  jobStatus,
  isExternalUser
}) => {
  const { t } = useTranslation();
  const [commentText, setCommentText] = useState(null);
  const [activeComment, setActiveComment] = useState({});
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comments, setComments] = useState([]);
  const [allCommentsFetched, setAllCommentsFetched] = useState(false);
  const [commentOffset, setCommentOffset] = useState(0);
  const limit = 1000;
  const [saveClickedWithoutResultID, setSaveClickedWithoutResultID] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState(status);

  const { 
    addCommentCallback, 
    loading: addingComment
  } = useAddDatasheetQuestionResultComment();
  const { 
    updateCommentCallback, 
    loading: updatingComment
  } = useUpdateDatasheetQuestionResultComment();
  const { 
    deleteCommentCallback, 
    loading: deletingComment
  } = useDeleteDatasheetQuestionResultComment();

  const [loadCommentsLazy, { data, loading }] = useDatasheetQuestionResultCommentsLazy({
    limit,
    offset: commentOffset,
    datasheetQuestionResultId: currentResult?.id,
  });

  useEffect(() => {
    if (data && data.datasheetQuestionResultComments.length) {
      setComments([ ...data.datasheetQuestionResultComments]);
      if (data.datasheetQuestionResultComments.length < limit) setAllCommentsFetched(true);
    }
  }, [data]);

  useEffect(() => {
    if (!allCommentsFetched && currentResult?.id) {
      loadCommentsLazy();
    }
  }, [commentOffset]);

  useEffect(() => {
    if (!allCommentsFetched && (data && data.datasheetQuestionResultComments.length)) {
      const newOffset = commentOffset + limit;
      setCommentOffset(newOffset);
    }
  }, [updatedScroll]);

  useEffect(() => {
    setCommentOffset(0);
    if(currentResult?.id)
      loadCommentsLazy();
  }, [currentResult]);
  
  const saveComments = async (resultId) =>{ 
    const hideCommentToExternalUsers = [VERIFIER_REVISION_UNDERWAY, REVIEWER_REVIEW_UNDERWAY];
    const commentVisibleForExternalUser = isExternalUser ? true : (!hideCommentToExternalUsers.includes(jobStatus));
      
    const { data: newData } = await addCommentCallback({datasheetQuestionResultId: resultId, comment: commentText, commentVisibleForExternalUser});
    const newComment = newData?.createDatasheetQuestionResultComment?.datasheetQuestionResultComment || {};
    const newComments = [ ...comments ];
    newComments.unshift(newComment);
    setComments(newComments);
    if(!isExternalUser) setUpdatedCommentStatus('open');
    notification.success(t('ADDED_COMMENT_SUCCESFULLY'));
    setShowCommentInput(false);
  }

  useEffect(() =>{
    if(saveClickedWithoutResultID){
      const results = resultUnique((storedResult || { data: [] }).data.concat(defaultResults));
      const resultId = results.find(result => result?.datasheetQuestionId === datasheetQuestionId)?.id;
      
      if(resultId){
        setSaveClickedWithoutResultID(false);
        const statusValue = results.find(result => result?.datasheetQuestionId === datasheetQuestionId)?.commentStatus;
        setUpdatedStatus(statusValue);
        saveComments(resultId);
      }
    }

  }, [storedResult])

  useEffect(() => {
    setShowCommentInput(false);
    setActiveComment({});
  }, [selectedQuestion]);

  const handleAddComment = async () => {
    try {
      let resultId = datasheetQuestionResultId;
      const results = resultUnique((storedResult || { data: [] }).data.concat(defaultResults))
      resultId = results.find(result => result?.datasheetQuestionId === datasheetQuestionId)?.id;
      
      if(resultId){
        saveComments(resultId);
      }else{
        setSaveClickedWithoutResultID(true);
        setResult();
      }
    } catch (e) {
      notification.error(t('ERROR_OCCURED'));
    }
  };

  const handleCommentDelete = async (id) => {
    try {
      await deleteCommentCallback({id});
      const newComments = [ ...comments ];
      const index = newComments.findIndex(comment => comment.id === id);
      newComments.splice(index, 1);
      setComments(newComments);
      notification.success(t('COMMENT_DELETED'));
    } catch (e) {
      notification.error(t('ERROR_OCCURED'));
    }
  };

  const handleUpdateComment = async (id, updatedComment) => {
    try {
      const { data: updatedData } = await updateCommentCallback({id, comment: updatedComment});
      const comment = updatedData?.updateDatasheetQuestionResultComment?.datasheetQuestionResultComment || {};
      const newComments = [ ...comments ];
      const index = newComments.findIndex(c => c.id === id);
      newComments[index] = comment;
      notification.success(t('COMMENT_UPDATED'));
      setActiveComment({});
      setComments(newComments);
    } catch (e) {
      notification.error(t('ERROR_OCCURED'));
    }
  };

  const showTextarea = () => {
    return (<>
      <s.CommentTextArea>
        <EiqTextarea
          id="edit-comment-input"
          name="comment"
          wrapperClassName="CommentInputWrapper" 
          rows={2}
          onChange={(e) => {
            setActiveComment({
              ...activeComment,
              updatedComment: e.target.value,
            });
          }}
          value={activeComment?.updatedComment}
          disabled={updatingComment}
        />
      </s.CommentTextArea>
      <s.ButtonWrapper>
        <s.CancelButton
          id="cancel-edit-comment"
          variant='btn--outline-link'
          label={t('CANCEL')}
          onClick={() => {
            setCommentText(null);
            setActiveComment({});
          }}
        />
        <s.SaveButton
          id="save-edit-comment"
          variant='btn--solid'
          label={t('SAVE')}
          onClick={() => handleUpdateComment(activeComment.id, activeComment.updatedComment)}
          disabled={activeComment?.updatedComment === activeComment?.comment || !activeComment?.updatedComment.trim()}
        />
      </s.ButtonWrapper>
    </>);
  };

  return <>
    <s.CommentsSection>
      <s.Title>{t('COMMENT')}</s.Title>
      { canAddComment && (
        <s.AddCommentIcon id="add-comment-icon" name="btnAdd" onClick={() => setShowCommentInput(true)} />
      )}
    </s.CommentsSection>
    { showCommentInput && (
      <s.CommentTextArea>
        <EiqTextarea
          id="add-comment-input"
          name="comment"
          wrapperClassName="CommentInputWrapper" 
          rows={2}
          onChange={(e) => setCommentText(e.target.value)}
        />
      </s.CommentTextArea>
    )}
    {showCommentInput && (<s.ButtonWrapper>
      <s.CancelButton
        id="cancel-add-comment"
        variant='btn--outline-link'
        label={t('CANCEL')}
        onClick={() => {
          setCommentText(null);
          setShowCommentInput(false);
        }}
      />
      <s.SaveButton
        id="save-add-comment"
        variant='btn--solid'
        label={t('SAVE')}
        onClick={handleAddComment}
        disabled={(saveClickedWithoutResultID || !commentText || addingComment )}
      />
      </s.ButtonWrapper>
    )}
    { comments.length > 0 || !isExternalUser ? comments.map(v => {
      const isUpdated = v.updatedAt > v.createdAt;
      return <div key={v.id} className='pl-8'>
        {(activeComment?.id === v.id) ? 
          showTextarea() : 
          <>
            <s.Comment>{v.comment}</s.Comment>
            <div className='d-flex'>
              <s.TitleSmall>{v.userName}</s.TitleSmall>
                <s.Separator />
                <s.CommentDate>
                  {`${isUpdated ? t('EDITED') : ''} ${moment(isUpdated ? v.updatedAt : v.createdAt).utc().format('DD MMM YYYY [at] HH:mm')}`}
                </s.CommentDate>
                {canAddComment && (currentUserId === v.createdBy) && (
                  <>
                    <s.Separator />
                    <s.ActionButton id="edit-comment-button" type="button" onClick={() => setActiveComment({ id: v.id, comment: v.comment, updatedComment: v.comment})}>{t('EDIT')}</s.ActionButton> 
                    <s.Separator />
                    <s.ActionButton id="delete-comment-button" 
                                    type="button" 
                                    onClick={() => handleCommentDelete(v.id)}
                                    disabled={deletingComment}
                    >{t('DELETE')}</s.ActionButton>
                  </>
                )}
            </div>
          </>
        }
      </div>
    }) : <div>{t('NO_COMMENTS_TO_VIEW')}</div>}
    { loading && <Loader /> }
  </>
}
